import SERIALIZED_FIELDS from './serialized-fields';
import { FORMAT_VERSION, PROFILE_KINDS } from './profile-document';

// `source` is for history only. The server logs it and the confirmation modal shows it, but it
// never identifies what gets written — that comes from the on-screen selection.
const buildSource = ({ environment, siteName, siteId, profileId }) => ({
  environment,
  siteName,
  siteId,
  profileId,
});

// Picks `fields` off `source`, dropping keys whose value is null or undefined so they never
// reach the document. A key present with a null value would be written into the target on
// upload; a key absent is left to the server's own default.
export const pickFields = (source, fields) =>
  fields.reduce((picked, field) => {
    const value = source?.[field];
    // Assigning rather than spreading: spreading an accumulator is O(n^2).
    if (value !== null && value !== undefined) picked[field] = value;
    return picked;
  }, {});

const EXPORT_PROFILE_FIELDS = ['sheetWriterClass', 'dateFormat', 'splitSheetsBySupplierSite', 'listName'];

const MULTILINE_HEADER_FIELDS = ['label', 'linkToStorefront', 'headerType', 'order', 'merges'];

const styleOf = (styles) => (styles?.color ? { color: styles.color } : undefined);

export const buildExportProfileDocument = ({ environment, site, exportProfile, mappings, exportedAt }) => ({
  formatVersion: FORMAT_VERSION,
  kind: PROFILE_KINDS.EXPORT,
  exportedAt,
  source: buildSource({
    environment,
    siteName: site.siteName,
    siteId: site._id,
    profileId: exportProfile._id,
  }),
  identity: { exportType: exportProfile.exportType },
  profile: {
    ...pickFields(exportProfile, EXPORT_PROFILE_FIELDS),
    multilineHeaders: (exportProfile.multilineHeaders ?? []).map((header) => ({
      ...pickFields(header, MULTILINE_HEADER_FIELDS),
      columnStyles: styleOf(header.columnStyles),
      headerStyles: styleOf(header.headerStyles),
    })),
  },
  mappings: (mappings ?? []).map((mapping) => ({
    ...pickFields(mapping, SERIALIZED_FIELDS.exportMapping),
    columnStyles: styleOf(mapping.columnStyles),
    headerStyles: styleOf(mapping.headerStyles),
  })),
});

const IMPORT_PROFILE_FIELDS = ['delimiter', 'useRootSiteForItems', 'headerNames', 'bypassItemValidation'];

const OPERATION_FIELDS = ['operator', 'params', 'staticParams'];

const DEFAULT_VALUE_FIELDS = ['defaultValue', 'defaultType'];

export const buildImportProfileDocument = ({ environment, site, importProfile, mappings, defaults, exportedAt }) => ({
  formatVersion: FORMAT_VERSION,
  kind: PROFILE_KINDS.IMPORT,
  exportedAt,
  source: buildSource({
    environment,
    siteName: site.siteName,
    siteId: site._id,
    profileId: importProfile._id,
  }),
  identity: {
    importType: importProfile.importType,
    // Absent rather than null for import types that do not use a name, so the server can tell
    // "no name" from "a name I could not read".
    ...(importProfile.importName ? { importName: importProfile.importName } : {}),
  },
  profile: {
    ...pickFields(importProfile, IMPORT_PROFILE_FIELDS),
    // dateFormats is a profile-level setting the user configures, unlike the derived
    // per-mapping dateFormats, so it does travel.
    dateFormats: importProfile.dateFormats ?? [],
  },
  mappings: (mappings ?? []).map((mapping) => {
    const serialized = pickFields(mapping, SERIALIZED_FIELDS.importMapping);
    if (!mapping.operation) return serialized;
    return { ...serialized, operation: pickFields(mapping.operation, OPERATION_FIELDS) };
  }),
  defaults: (defaults ?? []).map((importDefault) => ({
    ...pickFields(importDefault, SERIALIZED_FIELDS.importDefault),
    defaultValues: (importDefault.defaultValues ?? []).map((value) => pickFields(value, DEFAULT_VALUE_FIELDS)),
  })),
});

export const buildDataTableProfileDocument = ({ environment, dataTableProfile, exportedAt }) => ({
  formatVersion: FORMAT_VERSION,
  kind: PROFILE_KINDS.DATA_TABLE,
  exportedAt,
  source: buildSource({
    environment,
    siteName: dataTableProfile.siteName,
    siteId: dataTableProfile.siteId,
    profileId: dataTableProfile.id,
  }),
  identity: { dataTableName: dataTableProfile.dataTableName },
  profile: { dataTableType: dataTableProfile.dataTableType },
  columns: (dataTableProfile.columns ?? []).map((column) => pickFields(column, SERIALIZED_FIELDS.dataTableColumn)),
});
